/**
 * Persisted date display formats shared by storage, SDK values, and field readers.
 */
export type DateFormat =
  | 'default'
  | 'full'
  | 'month-day-year'
  | 'day-month-year'
  | 'year-month-day';

/**
 * Enum-compatible runtime names for persisted date display formats.
 */
export const DateFormat: Readonly<{
  DEFAULT: DateFormat;
  FULL: DateFormat;
  MONTH_DAY_YEAR: DateFormat;
  DAY_MONTH_YEAR: DateFormat;
  YEAR_MONTH_DAY: DateFormat;
}> = {
  DEFAULT: 'default',
  FULL: 'full',
  MONTH_DAY_YEAR: 'month-day-year',
  DAY_MONTH_YEAR: 'day-month-year',
  YEAR_MONTH_DAY: 'year-month-day'
};

export const DateFormatPossibleValues = Object.values(DateFormat);

/**
 * Persisted time display formats shared by storage, SDK values, and field readers.
 */
export type TimeFormat = '12' | '24';

/**
 * Enum-compatible runtime names for persisted time display formats.
 */
export const TimeFormat: Readonly<{
  TWELVE_HOUR: TimeFormat;
  TWENTY_FOUR_HOUR: TimeFormat;
}> = {
  TWELVE_HOUR: '12',
  TWENTY_FOUR_HOUR: '24'
};

export const TimeFormatPossibleValues = Object.values(TimeFormat);

/**
 * Additional Select colors introduced by the ADS refresh.
 */
export const ADSRefresh2025Colors = [
  'limeDark',
  'magentaDark',
  'orangeDark',
  'limeLight',
  'magentaLight',
  'orangeLight'
];

/**
 * Persisted Select appearance tokens in their canonical palette order.
 */
export const SelectOptionColors = [
  'blueDark',
  'redDark',
  'yellowDark',
  'greenDark',
  'tealDark',
  'purpleDark',
  'greyDark',
  'blueLight',
  'redLight',
  'yellowLight',
  'greenLight',
  'tealLight',
  'purpleLight',
  'greyLight',
  ...ADSRefresh2025Colors
] as const;

/**
 * Canonical dark Select appearance tokens.
 */
export const darkSelectOptionColors = SelectOptionColors.filter((color) =>
  color.endsWith('Dark')
);

/**
 * Canonical light Select appearance tokens.
 */
export const lightSelectOptionColors = SelectOptionColors.filter((color) =>
  color.endsWith('Light')
);

/**
 * One persisted Select appearance token.
 */
export type SelectOptionColor = (typeof SelectOptionColors)[number];
